import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Alert,
  Typography,
  LinearProgress,
  Stack,
} from '@mui/material'
import { Add, Edit, Delete, Warning, Visibility, AttachFile, Download, CloudUpload, FilterList, Clear } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, Modal, ExportButtons } from '@components/common'
import { usePermissions } from '@context/PermissionContext'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'

const CONTRACT_TYPES = [
  { value: 'warranty', label: 'Warranty' },
  { value: 'maintenance', label: 'Maintenance Contract' },
  { value: 'calibration', label: 'Calibration Contract' },
  { value: 'full_service', label: 'Full Service Contract' },
  { value: 'framework', label: 'Framework Contract' },
  { value: 'comprehensive', label: 'Comprehensive Contract' },
]

const ContractsList = () => {
  const { canCreate, canEdit, canDelete } = usePermissions()
  const [contracts, setContracts] = useState([])
  const [assets, setAssets] = useState([])
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedContract, setSelectedContract] = useState(null)
  const [expiringSoon, setExpiringSoon] = useState([])
  const [saving, setSaving] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Filter state
  const [filters, setFilters] = useState({
    contract_type: '',
    is_active: '',
    vendor: '',
  })

  const [formData, setFormData] = useState({
    asset: '',
    vendor: '',
    contract_number: '',
    contract_type: 'maintenance',
    start_date: '',
    end_date: '',
    cost: '',
    terms: '',
    is_active: true,
    document: null,
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filters.contract_type) params.contract_type = filters.contract_type
      if (filters.is_active !== '') params.is_active = filters.is_active
      if (filters.vendor) params.vendor = filters.vendor

      const [contractsRes, assetsRes, vendorsRes] = await Promise.all([
        api.get('/assets/contracts/', { params }),
        api.get('/assets/assets/'),
        api.get('/vendors/'),
      ])
      const contractsData = contractsRes.data.results || contractsRes.data
      setContracts(contractsData)
      setAssets(assetsRes.data.results || assetsRes.data)
      setVendors(vendorsRes.data.results || vendorsRes.data)

      // Calculate expiring soon (within 30 days)
      const today = new Date()
      const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
      const expiring = contractsData.filter((c) => {
        if (!c.end_date || !c.is_active) return false
        const endDate = new Date(c.end_date)
        return endDate <= thirtyDays && endDate >= today
      })
      setExpiringSoon(expiring)
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load contracts data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  // Export columns definition
  const exportColumns = useMemo(() => [
    { field: 'contract_number', headerName: 'Contract Ref #' },
    { field: 'contract_type', headerName: 'Type', renderValue: (v) => CONTRACT_TYPES.find((t) => t.value === v)?.label || v },
    { field: 'asset_name', headerName: 'Asset' },
    { field: 'vendor_name', headerName: 'Vendor' },
    { field: 'start_date', headerName: 'Start Date', renderValue: (v) => v ? formatDate(v) : '-' },
    { field: 'end_date', headerName: 'End Date', renderValue: (v) => v ? formatDate(v) : '-' },
    { field: 'cost', headerName: 'Cost', renderValue: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '-' },
    { field: 'is_active', headerName: 'Active', renderValue: (v) => v ? 'Yes' : 'No' },
  ], [])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    setFilters({ contract_type: '', is_active: '', vendor: '' })
  }

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length

  const handleAdd = () => {
    setSelectedContract(null)
    const today = new Date().toISOString().split('T')[0]
    const nextYear = new Date()
    nextYear.setFullYear(nextYear.getFullYear() + 1)
    setFormData({
      asset: '',
      vendor: '',
      contract_number: '',
      contract_type: 'maintenance',
      start_date: today,
      end_date: nextYear.toISOString().split('T')[0],
      cost: '',
      terms: '',
      is_active: true,
      document: null,
    })
    setFormOpen(true)
  }

  const handleEdit = (contract) => {
    setSelectedContract(contract)
    setFormData({
      asset: contract.asset || '',
      vendor: contract.vendor || '',
      contract_number: contract.contract_number || '',
      contract_type: contract.contract_type || 'maintenance',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      cost: contract.cost || '',
      terms: contract.terms || '',
      is_active: contract.is_active !== false,
      document: null,
    })
    setFormOpen(true)
  }

  const handleView = (contract) => {
    setSelectedContract(contract)
    setDetailOpen(true)
  }

  const handleSave = async () => {
    if (!formData.asset || !formData.contract_number || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      // Use FormData for file uploads
      const payload = new FormData()
      payload.append('asset', formData.asset)
      payload.append('contract_number', formData.contract_number)
      payload.append('contract_type', formData.contract_type)
      payload.append('start_date', formData.start_date)
      payload.append('end_date', formData.end_date)
      payload.append('is_active', formData.is_active)
      if (formData.vendor) payload.append('vendor', formData.vendor)
      if (formData.cost) payload.append('cost', formData.cost)
      if (formData.terms) payload.append('terms', formData.terms)
      if (formData.document) payload.append('document', formData.document)

      const config = { headers: { 'Content-Type': 'multipart/form-data' } }
      if (selectedContract) {
        await api.patch(`/assets/contracts/${selectedContract.id}/`, payload, config)
        toast.success('Contract updated')
      } else {
        await api.post('/assets/contracts/', payload, config)
        toast.success('Contract created')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving contract:', error)
      toast.error(error.response?.data?.error || 'Failed to save contract')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (contract) => {
    if (!window.confirm(`Delete contract ${contract.contract_number}?`)) return
    try {
      await api.delete(`/assets/contracts/${contract.id}/`)
      toast.success('Contract deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting contract:', error)
      toast.error('Failed to delete contract')
    }
  }

  const getContractStatus = (contract) => {
    if (!contract.is_active) return { label: 'Inactive', color: 'default' }
    if (!contract.end_date) return { label: 'Active', color: 'success' }

    const today = new Date()
    const endDate = new Date(contract.end_date)
    const daysUntil = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24))

    if (daysUntil < 0) return { label: 'Expired', color: 'error' }
    if (daysUntil <= 30) return { label: `${daysUntil}d left`, color: 'warning' }
    return { label: 'Active', color: 'success' }
  }

  const columns = [
    { field: 'contract_number', headerName: 'Contract Ref #', sortable: true },
    {
      field: 'contract_type',
      headerName: 'Type',
      renderCell: (v) => (
        <Chip label={CONTRACT_TYPES.find((t) => t.value === v)?.label || v} size="small" />
      ),
    },
    {
      field: 'asset',
      headerName: 'Asset',
      renderCell: (_, row) => row.asset_name || '-',
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      renderCell: (_, row) => row.vendor_name || '-',
    },
    {
      field: 'start_date',
      headerName: 'Start',
      renderCell: (v) => v ? formatDate(v) : '-',
      sortable: true,
    },
    {
      field: 'end_date',
      headerName: 'End',
      renderCell: (v) => v ? formatDate(v) : '-',
      sortable: true,
    },
    {
      field: 'cost',
      headerName: 'Cost',
      renderCell: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (_, row) => {
        const status = getContractStatus(row)
        return <Chip label={status.label} size="small" color={status.color} />
      },
    },
    {
      field: 'document',
      headerName: 'Document',
      width: 80,
      align: 'center',
      renderCell: (_, row) => row.document_url ? (
        <IconButton
          size="small"
          color="primary"
          onClick={(e) => {
            e.stopPropagation()
            window.open(row.document_url, '_blank')
          }}
          title="Download Document"
        >
          <Download fontSize="small" />
        </IconButton>
      ) : '-',
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(row) }}>
            <Visibility fontSize="small" />
          </IconButton>
          {canEdit('service_contracts') && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {canDelete('service_contracts') && (
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ]

  // Summary stats
  const activeContracts = contracts.filter((c) => c.is_active).length
  const expiringContracts = expiringSoon.length
  const totalValue = contracts.reduce((sum, c) => sum + (parseFloat(c.cost) || 0), 0)

  return (
    <Box>
      <PageHeader
        title="Service Contracts"
        subtitle="Manage asset service contracts and warranties"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Vendors', path: '/vendors' },
          { label: 'Contracts' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <ExportButtons
              data={contracts}
              columns={exportColumns}
              title="Service Contracts List"
              filename="contracts"
              filters={{
                Type: filters.contract_type ? CONTRACT_TYPES.find(t => t.value === filters.contract_type)?.label : null,
                Active: filters.is_active !== '' ? (filters.is_active === 'true' ? 'Yes' : 'No') : null,
                Vendor: filters.vendor ? vendors.find(v => v.id === parseInt(filters.vendor))?.name : null,
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={activeFilterCount > 0 ? 'primary' : 'inherit'}
            >
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>
            {canCreate('service_contracts') && (
              <Button startIcon={<Add />} onClick={handleAdd}>
                Add Contract
              </Button>
            )}
          </Stack>
        }
      />

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="primary">
                {activeContracts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Contracts
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {expiringContracts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expiring Soon
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="success.main">
                UGX {totalValue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Contract Value
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {expiringSoon.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          {expiringSoon.length} contract(s) expiring within the next 30 days
        </Alert>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card sx={{ mb: 2, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Contract Type</InputLabel>
                <Select
                  value={filters.contract_type}
                  onChange={(e) => handleFilterChange('contract_type', e.target.value)}
                  label="Contract Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {CONTRACT_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={filters.vendor}
                  onChange={(e) => handleFilterChange('vendor', e.target.value)}
                  label="Vendor"
                >
                  <MenuItem value="">All</MenuItem>
                  {vendors.map((v) => (
                    <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Active</InputLabel>
                <Select
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                  label="Active"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={contracts}
          loading={loading}
          searchPlaceholder="Search contracts..."
          emptyMessage="No contracts found"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedContract ? 'Edit Contract' : 'Add Contract'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedContract ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Asset</InputLabel>
              <Select
                value={formData.asset}
                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                label="Asset"
              >
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Contract Reference Number"
              value={formData.contract_number}
              onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Contract Type</InputLabel>
              <Select
                value={formData.contract_type}
                onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                label="Contract Type"
              >
                {CONTRACT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                label="Vendor"
              >
                <MenuItem value="">None</MenuItem>
                {vendors.filter((v) => v.is_active).map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Contract Value (UGX)"
              type="number"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Terms & Conditions"
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Contract Document
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                {formData.document ? formData.document.name : (selectedContract?.document_url ? 'Replace Document' : 'Upload Document')}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      setFormData({ ...formData, document: e.target.files[0] })
                    }
                  }}
                />
              </Button>
              {selectedContract?.document_url && !formData.document && (
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Current: {selectedContract.document_url.split('/').pop()}
                  </Typography>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => window.open(selectedContract.document_url, '_blank')}
                    title="Download"
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Contract Details"
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
        }
      >
        {selectedContract && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Contract Reference Number</Box>
                <Box fontWeight={600}>{selectedContract.contract_number}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Type</Box>
                <Chip
                  label={CONTRACT_TYPES.find((t) => t.value === selectedContract.contract_type)?.label}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Asset</Box>
                <Box fontWeight={600}>{selectedContract.asset_name || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Vendor</Box>
                <Box fontWeight={600}>{selectedContract.vendor_name || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Start Date</Box>
                <Box fontWeight={600}>
                  {selectedContract.start_date
                    ? formatDate(selectedContract.start_date)
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">End Date</Box>
                <Box fontWeight={600}>
                  {selectedContract.end_date
                    ? formatDate(selectedContract.end_date)
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Value</Box>
                <Box fontWeight={600}>
                  {selectedContract.cost ? `UGX ${Number(selectedContract.cost).toLocaleString()}` : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Status</Box>
                <Chip
                  label={getContractStatus(selectedContract).label}
                  size="small"
                  color={getContractStatus(selectedContract).color}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            {selectedContract.terms && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Terms & Conditions</Box>
                  <Box mt={0.5}>{selectedContract.terms}</Box>
                </Box>
              </Grid>
            )}
            {selectedContract.document_url && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Contract Document</Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={() => window.open(selectedContract.document_url, '_blank')}
                    sx={{ mt: 0.5 }}
                  >
                    Download Document
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>
    </Box>
  )
}

export default ContractsList
